<template>
  <div class="naming-match-panel">
    <!-- 未满足条件提示 -->
    <div v-if="!canAnalyze" class="no-data-hint">
      <MysticalCard>
        <div class="hint-content">
          <div class="hint-icon">🔮</div>
          <h3>命名匹配分析</h3>
          <p>请先完成以下操作：</p>
          <ul>
            <li v-if="!hasBazi" :class="{ done: hasBazi }">
              <span :class="hasBazi ? 'done-icon' : 'todo-icon'">{{ hasBazi ? '✅' : '⬜' }}</span>
              在「八字命盘」中输入出生信息并查看八字
            </li>
            <li v-if="!hasNaming" :class="{ done: hasNaming }">
              <span :class="hasNaming ? 'done-icon' : 'todo-icon'">{{ hasNaming ? '✅' : '⬜' }}</span>
              在「取名分析」中输入姓名并查看五格分析
            </li>
          </ul>
          <p class="hint-desc">完成两步后，系统将自动分析八字五行与姓名五行的互补关系。</p>
        </div>
      </MysticalCard>
    </div>

    <!-- 分析结果 -->
    <div v-else class="match-result">
      <!-- 命主信息卡片 -->
      <MysticalCard class="info-card">
        <div class="card-title">👤 命主信息</div>
        <div class="info-grid">
          <div class="info-row">
            <div class="info-item">
              <span class="info-label">日主</span>
              <el-tag :type="getElementTagType(baziData.dayMaster?.element)" size="large">
                {{ baziData.tianGan?.day }}{{ baziData.dayMaster?.element }}
              </el-tag>
              <el-tag :type="baziData.dayMaster?.strength > 0 ? 'danger' : 'warning'" size="small" class="strength-tag">
                {{ baziData.dayMaster?.strength > 0 ? '身旺' : '身弱' }}
              </el-tag>
            </div>
            <div class="info-item">
              <span class="info-label">生肖</span>
              <span class="info-value">{{ baziData.zodiac || '-' }}</span>
            </div>
          </div>
          <div class="info-row">
            <div class="info-item">
              <span class="info-label">八字</span>
              <span class="info-value bazi-text">
                {{ baziData.tianGan?.year }}{{ baziData.diZhi?.year }}
                {{ baziData.tianGan?.month }}{{ baziData.diZhi?.month }}
                {{ baziData.tianGan?.day }}{{ baziData.diZhi?.day }}
                {{ baziData.tianGan?.hour }}{{ baziData.diZhi?.hour }}
              </span>
            </div>
          </div>
          <div class="info-row">
            <div class="info-item">
              <span class="info-label">喜用神</span>
              <div class="shen-tags">
                <el-tag
                  v-for="item in favorableShens"
                  :key="item.element"
                  type="success"
                  effect="dark"
                  class="shen-tag"
                >
                  {{ item.element }}({{ item.shen }})
                </el-tag>
                <span v-if="favorableShens.length === 0" class="no-data">-</span>
              </div>
            </div>
          </div>
          <div class="info-row">
            <div class="info-item">
              <span class="info-label">忌神</span>
              <div class="shen-tags">
                <el-tag
                  v-for="item in unfavorableShens"
                  :key="item.element"
                  type="danger"
                  effect="dark"
                  class="shen-tag"
                >
                  {{ item.element }}({{ item.shen }})
                </el-tag>
                <span v-if="unfavorableShens.length === 0" class="no-data">-</span>
              </div>
            </div>
          </div>
        </div>
      </MysticalCard>

      <!-- 五格 × 日主匹配 -->
      <MysticalCard class="match-card">
        <div class="card-title">🎯 五格 × 日主匹配分析</div>
        <p class="match-desc">分析姓名五格五行与日主的十神关系，判断姓名对命局运势的影响。</p>
        <div class="match-grid">
          <div
            v-for="item in gridMatches"
            :key="item.name"
            class="match-card-item"
            :class="item.isFavorable ? 'favorable' : 'unfavorable'"
          >
            <div class="card-item-header">
              <span class="grid-label">{{ item.label }}</span>
              <span class="grid-value-tag">
                <el-tag :type="getElementTagType(item.element)" size="small">{{ item.value }}{{ item.element }}</el-tag>
              </span>
              <span class="match-status-icon">{{ item.isFavorable ? '✅' : (item.isNeutral ? '⚠️' : '❌') }}</span>
            </div>
            <div class="card-item-body">
              <el-tag
                :type="item.isFavorable ? 'success' : (item.isNeutral ? 'warning' : 'danger')"
                size="small"
                class="shishen-tag"
              >
                {{ item.shiShen }}
              </el-tag>
            </div>
            <div class="card-item-footer">
              <span class="match-effect">{{ item.effect }}</span>
            </div>
          </div>
        </div>
      </MysticalCard>

      <!-- 三才五行分析 -->
      <MysticalCard class="talents-card">
        <div class="card-title">🔮 三才五行分析</div>
        <div class="talents-grid">
          <div class="talent-item">
            <span class="talent-label">天才</span>
            <el-tag :type="getElementTagType(talentsElements.tian)" size="large">
              {{ talentsElements.tian }}
            </el-tag>
          </div>
          <div class="talent-arrow">→</div>
          <div class="talent-item">
            <span class="talent-label">人才</span>
            <el-tag :type="getElementTagType(talentsElements.ren)" size="large">
              {{ talentsElements.ren }}
            </el-tag>
          </div>
          <div class="talent-arrow">→</div>
          <div class="talent-item">
            <span class="talent-label">地才</span>
            <el-tag :type="getElementTagType(talentsElements.di)" size="large">
              {{ talentsElements.di }}
            </el-tag>
          </div>
        </div>
        <div class="talents-result">
          <div class="talents-type" :class="getTalentsTypeClass(talentsData?.type)">
            三才吉凶：{{ talentsData?.type || '未知' }}
          </div>
          <div v-if="talentsData?.description" class="talents-desc">
            {{ talentsData.description }}
          </div>
          <div class="talents-evaluation">
            <span class="eval-label">组合评价：</span>
            <span>{{ talentsEvaluation }}</span>
          </div>
        </div>
      </MysticalCard>

      <!-- 综合评分 -->
      <MysticalCard class="score-card">
        <div class="card-title">📊 综合评分</div>
        <div class="score-content">
          <div class="score-ring">
            <el-progress
              type="dashboard"
              :percentage="totalScore"
              :width="140"
              :stroke-width="12"
              :color="getScoreColor(totalScore)"
            >
              <template #default="{ percentage }">
                <span class="score-number">{{ percentage }}</span>
                <span class="score-unit">/100</span>
              </template>
            </el-progress>
          </div>
          <div class="score-verdict" :class="getVerdictClass(totalScore)">
            <span class="verdict-icon">{{ getVerdictIcon(totalScore) }}</span>
            <span class="verdict-text">{{ getVerdictText(totalScore) }}</span>
          </div>
        </div>
        <div class="score-detail">
          <p class="score-summary">{{ getScoreSummary(totalScore) }}</p>
          <div class="score-breakdown">
            <div class="breakdown-item" v-for="item in scoreBreakdown" :key="item.label">
              <span class="breakdown-label">{{ item.label }}</span>
              <span class="breakdown-value" :class="item.value >= 0 ? 'positive' : 'negative'">
                {{ item.value >= 0 ? '+' : '' }}{{ item.value }}
              </span>
            </div>
          </div>
        </div>
        <div v-if="suggestion" class="suggestion">
          <span class="suggestion-label">💡 建议：</span>
          {{ suggestion }}
        </div>
      </MysticalCard>
    </div>
  </div>
</template>

<script setup>
import { computed, toRef } from 'vue'
import { MysticalCard } from '@/components/common'

const props = defineProps({
  baziResult: {
    type: Object,
    default: () => null
  },
  namingResult: {
    type: Object,
    default: () => null
  },
  talentsResult: {
    type: Object,
    default: () => null
  },
  fullName: {
    type: String,
    default: ''
  }
})

// ============= 常量映射 =============

// 天干→五行
const GAN_TO_ELEMENT = {
  '甲': '木', '乙': '木', '丙': '火', '丁': '火', '戊': '土',
  '己': '土', '庚': '金', '辛': '金', '壬': '水', '癸': '水'
}

// 五行相生相克关系
const ELEMENT_SHENG = { '木': '水', '火': '木', '土': '火', '金': '土', '水': '金' }  // 生我者
const ELEMENT_KE = { '木': '金', '火': '水', '土': '木', '金': '火', '水': '土' }     // 克我者
const ELEMENT_WO_SHENG = { '木': '火', '火': '土', '土': '金', '金': '水', '水': '木' } // 我生者
const ELEMENT_WO_KE = { '木': '土', '火': '金', '土': '水', '金': '木', '水': '火' }   // 我克者

// 数尾数→五行映射
const ELEMENT_MAP = {
  1: '木', 2: '木', 3: '火', 4: '火', 5: '土', 6: '土',
  7: '金', 8: '金', 9: '水', 0: '水'
}

// 五行相生顺序
const SHENG_CYCLE = ['木', '火', '土', '金', '水']

// ============= 辅助函数 =============

// 数理→五行
const getNumberElement = (number) => {
  if (!number) return '未知'
  const lastDigit = number % 10
  return ELEMENT_MAP[lastDigit] || '未知'
}

// 获取十神关系
const getShiShenRelation = (dayElement, targetElement) => {
  if (!dayElement || !targetElement || dayElement === '未知' || targetElement === '未知') {
    return { shen: '未知', type: 'neutral' }
  }
  if (dayElement === targetElement) return { shen: '比肩', type: 'bijie' } // 同我

  const shengMe = ELEMENT_SHENG[dayElement] // 生我者
  const keMe = ELEMENT_KE[dayElement]       // 克我者
  const woSheng = ELEMENT_WO_SHENG[dayElement] // 我生者
  const woKe = ELEMENT_WO_KE[dayElement]     // 我克者

  if (targetElement === shengMe) return { shen: '正印/偏印', type: 'yin' }      // 生我
  if (targetElement === keMe) return { shen: '正官/七杀', type: 'guan' }       // 克我
  if (targetElement === woSheng) return { shen: '食神/伤官', type: 'shiShang' } // 我生
  if (targetElement === woKe) return { shen: '正财/偏财', type: 'cai' }        // 我克

  return { shen: '未知', type: 'neutral' }
}

// ============= 响应式数据 =============

const baziData = computed(() => props.baziResult?.value || props.baziResult || {})
const namingData = computed(() => props.namingResult?.value || props.namingResult || {})
const talentsData = computed(() => props.talentsResult?.value || props.talentsResult || null)

const hasBazi = computed(() => {
  const d = baziData.value
  return d && d.dayMaster && d.tianGan && d.tianGan.day
})

const hasNaming = computed(() => {
  const d = namingData.value
  return d && d.renGe && d.diGe
})

const canAnalyze = computed(() => hasBazi.value && hasNaming.value)

// ============= 喜用神/忌神判定 =============

const favorableShens = computed(() => {
  const dm = baziData.value.dayMaster
  if (!dm || !dm.element) return []

  const element = dm.element
  const isStrong = dm.strength > 0

  if (isStrong) {
    // 身旺：喜食伤(泄秀)+财星(耗身)
    const shiShang = ELEMENT_WO_SHENG[element]
    const cai = ELEMENT_WO_KE[element]
    const result = []
    if (shiShang) result.push({ element: shiShang, shen: '食伤' })
    if (cai) result.push({ element: cai, shen: '财星' })
    return result
  } else {
    // 身弱：喜印星(生我)+比劫(同我)
    const yin = ELEMENT_SHENG[element]
    const result = [
      { element: element, shen: '比劫' }
    ]
    if (yin) result.push({ element: yin, shen: '印星' })
    return result
  }
})

const unfavorableShens = computed(() => {
  const dm = baziData.value.dayMaster
  if (!dm || !dm.element) return []

  const element = dm.element
  const isStrong = dm.strength > 0

  if (isStrong) {
    // 身旺：忌比劫(帮身)+印星(生身)
    const yin = ELEMENT_SHENG[element]
    const result = [
      { element: element, shen: '比劫' }
    ]
    if (yin) result.push({ element: yin, shen: '印星' })
    return result
  } else {
    // 身弱：忌官杀(克我)+财星(我克)+食伤(泄我)
    const guan = ELEMENT_KE[element]
    const cai = ELEMENT_WO_KE[element]
    const shiShang = ELEMENT_WO_SHENG[element]
    const result = []
    if (guan) result.push({ element: guan, shen: '官杀' })
    if (cai) result.push({ element: cai, shen: '财星' })
    if (shiShang) result.push({ element: shiShang, shen: '食伤' })
    return result
  }
})

// ============= 五格×日主匹配 =============

const gridMatches = computed(() => {
  const dm = baziData.value.dayMaster
  if (!dm || !dm.element) return []
  const n = namingData.value
  if (!n) return []

  const dayElement = dm.element
  const favorableElements = favorableShens.value.map(s => s.element)
  const unfavorableElements = unfavorableShens.value.map(s => s.element)

  const grids = [
    { key: 'tianGe', label: '天格', effect: '早年运势' },
    { key: 'renGe', label: '人格', effect: '中年运势（最关键）' },
    { key: 'diGe', label: '地格', effect: '晚年运势' },
    { key: 'zongGe', label: '总格', effect: '一生总运势' },
    { key: 'waiGe', label: '外格', effect: '社交运势' }
  ]

  return grids.map(grid => {
    const value = n[grid.key]
    const element = getNumberElement(value)
    const relation = getShiShenRelation(dayElement, element)

    let effectText = ''
    const shiShenName = relation.shen

    if (relation.type === 'bijie') {
      effectText = '比肩帮扶'
    } else if (relation.type === 'yin') {
      effectText = '印星生扶'
    } else if (relation.type === 'guan') {
      effectText = '官杀克身'
    } else if (relation.type === 'shiShang') {
      effectText = '食伤泄秀'
    } else if (relation.type === 'cai') {
      effectText = '财星耗身'
    } else {
      effectText = '关系不明'
    }

    const isFav = favorableElements.includes(element)
    const isUnfav = unfavorableElements.includes(element)

    return {
      name: grid.key,
      label: grid.label,
      value: value,
      element: element,
      shiShen: shiShenName,
      effect: `${grid.effect}：${effectText}`,
      isFavorable: isFav,
      isUnfavorable: isUnfav,
      isNeutral: !isFav && !isUnfav
    }
  })
})

// ============= 三才五行分析 =============

const talentsElements = computed(() => {
  const n = namingData.value
  if (!n) return { tian: '未知', ren: '未知', di: '未知' }
  return {
    tian: getNumberElement(n.tianGe),
    ren: getNumberElement(n.renGe),
    di: getNumberElement(n.diGe)
  }
})

const talentsEvaluation = computed(() => {
  const { tian, ren, di } = talentsElements.value
  const dm = baziData.value.dayMaster
  if (!dm || !dm.element || tian === '未知' || ren === '未知' || di === '未知') {
    return '数据不足，无法评价'
  }

  const dayElement = dm.element
  const favorableElements = favorableShens.value.map(s => s.element)
  const unfavorableElements = unfavorableShens.value.map(s => s.element)

  // 分析三才五行与日主的关系
  const allElements = [tian, ren, di]
  const favCount = allElements.filter(e => favorableElements.includes(e)).length
  const unfavCount = allElements.filter(e => unfavorableElements.includes(e)).length

  const combo = `${tian}${ren}${di}`

  if (favCount >= 2) {
    return `${combo} → 三才五行偏助日主，命局得力`
  } else if (unfavCount >= 2) {
    return `${combo} → 三才五行克制日主，需注意`
  } else if (favCount === 1) {
    return `${combo} → 三才五行有助有克，基本平衡`
  } else {
    return `${combo} → 三才五行与日主关系一般`
  }
})

// ============= 综合评分 =============

const scoreBreakdown = computed(() => {
  const items = []

  // 1. 喜用神匹配（每个+10分，最多50分）
  let favMatchCount = 0
  gridMatches.value.forEach(item => {
    if (item.isFavorable) favMatchCount++
  })
  items.push({ label: '喜用神匹配', value: favMatchCount * 10 })

  // 2. 忌神冲突（每个-10分，最多-50分）
  let unfavMatchCount = 0
  gridMatches.value.forEach(item => {
    if (item.isUnfavorable) unfavMatchCount++
  })
  items.push({ label: '忌神冲突', value: -unfavMatchCount * 10 })

  // 3. 三才吉凶
  const talentsType = talentsData.value?.type || ''
  let talentsScore = 0
  if (talentsType.includes('大吉')) talentsScore = 15
  else if (talentsType.includes('中吉')) talentsScore = 10
  else if (talentsType === '吉') talentsScore = 5
  else if (talentsType.includes('吉多于凶')) talentsScore = 3
  else if (talentsType.includes('吉凶参半')) talentsScore = 0
  else if (talentsType.includes('凶多于吉')) talentsScore = -5
  else if (talentsType.includes('大凶')) talentsScore = -15
  items.push({ label: '三才吉凶', value: talentsScore })

  // 4. 天格/人格/地格数理吉凶
  const n = namingData.value
  if (n) {
    const fortuneScores = { '大吉': 10, '吉': 5, '半吉': 0, '凶': -5, '大凶': -10 }
    const keyGrids = [n.tianGe, n.renGe, n.diGe]
    let gridScore = 0
    keyGrids.forEach(val => {
      const fortune = getGridFortune(val)
      gridScore += fortuneScores[fortune] || 0
    })
    items.push({ label: '三格数理吉凶', value: gridScore })
  }

  return items
})

const totalScore = computed(() => {
  const raw = scoreBreakdown.value.reduce((sum, item) => sum + item.value, 0)
  return Math.max(0, Math.min(100, raw + 50)) // 基础分50，clamp到0-100
})

// 数理吉凶查询（从本地计算）
const getGridFortune = (gridValue) => {
  if (!gridValue) return '凶'
  // 简化的数理吉凶判断（基于尾数和常见规则）
  const num = gridValue > 81 ? gridValue - 80 : gridValue
  // 大吉数理
  const daji = [1, 3, 5, 6, 7, 8, 11, 13, 15, 16, 17, 18, 21, 23, 24, 25, 29, 31, 32, 33, 35, 37, 39, 41, 45, 47, 48, 52, 57, 61, 63, 65, 67, 68, 73, 75, 81]
  // 吉数理
  const ji = [26, 27, 30, 38, 51, 55, 56, 64, 71]
  // 凶数理
  const xiong = [2, 4, 9, 10, 12, 14, 19, 20, 22, 28, 34, 36, 40, 42, 43, 44, 46, 49, 50, 53, 54, 58, 59, 60, 62, 66, 69, 70, 72, 74, 76, 77, 78, 79, 80]
  // 大凶数理
  const daxiong = []

  if (daji.includes(num)) return '大吉'
  if (ji.includes(num)) return '吉'
  if (daxiong.includes(num)) return '大凶'
  if (xiong.includes(num)) return '凶'
  return '半吉'
}

// ============= 建议 =============

const suggestion = computed(() => {
  const score = totalScore.value
  const dm = baziData.value.dayMaster
  if (!dm || !dm.element) return ''

  const fav = favorableShens.value.map(s => s.element).join('、')
  const unfav = unfavorableShens.value.map(s => s.element).join('、')

  if (score >= 70) {
    return `姓名五行与命局配合良好，喜${fav}五行能量得到补充。保持当前姓名格局即可。`
  } else if (score >= 50) {
    if (fav) {
      return `命局喜${fav}五行，姓名中可适当补充这些五行能量的字。当前姓名基本可用。`
    }
    return '当前姓名与命局基本平衡，无明显冲突。'
  } else if (score >= 30) {
    return `姓名与命局配合一般。建议取名时多用${fav}五行属性的字，避免${unfav}五行。`
  } else {
    return `姓名与命局存在明显冲突，${unfav}五行过重。强烈建议选择${fav}五行属性的字名。`
  }
})

// ============= UI 辅助 =============

const getElementTagType = (element) => {
  const map = { '木': 'success', '火': 'danger', '土': 'warning', '金': 'info', '水': '' }
  return map[element] || 'info'
}

const getTalentsTypeClass = (type) => {
  if (!type) return ''
  if (type.includes('大吉')) return 'type-daji'
  if (type.includes('中吉')) return 'type-zhongji'
  if (type === '吉') return 'type-ji'
  if (type.includes('凶多于吉') || type.includes('大凶')) return 'type-xiong'
  return 'type-normal'
}

const getScoreColor = (score) => {
  if (score >= 70) return '#67C23A'
  if (score >= 50) return '#E6A23C'
  if (score >= 30) return '#F56C6C'
  return '#F56C6C'
}

const getVerdictIcon = (score) => {
  if (score >= 70) return '✅'
  if (score >= 50) return '⚠️'
  if (score >= 30) return '⚠️'
  return '❌'
}

const getVerdictText = (score) => {
  if (score >= 70) return '压得住，姓名助运'
  if (score >= 50) return '基本平衡，无明显冲突'
  if (score >= 30) return '压不太住，有克制'
  return '压不住，姓名与命局相冲'
}

const getVerdictClass = (score) => {
  if (score >= 70) return 'verdict-good'
  if (score >= 50) return 'verdict-ok'
  if (score >= 30) return 'verdict-warn'
  return 'verdict-bad'
}

const getScoreSummary = (score) => {
  if (score >= 70) return '姓名五行与八字命局配合良好，姓名能有效助运，命局五行得到补充。'
  if (score >= 50) return '姓名与命局基本平衡，既无明显助力也无严重冲突，属中性格局。'
  if (score >= 30) return '姓名五行对命局有一定的克制作用，可能在某些方面产生不利影响。'
  return '姓名五行与命局存在较大冲突，建议考虑更符合命局五行的名字。'
}
</script>

<style lang="scss" scoped>
.naming-match-panel {
  padding: 8px 0;
}

.no-data-hint {
  .hint-content {
    text-align: center;
    padding: 40px 20px;

    .hint-icon {
      font-size: 3rem;
      margin-bottom: 16px;
    }

    h3 {
      font-size: 1.4rem;
      color: var(--color-text, #e0e0e0);
      margin-bottom: 16px;
    }

    p {
      color: var(--color-text-secondary, #aaa);
      margin-bottom: 12px;
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 16px 0;
      text-align: left;
      max-width: 400px;
      margin-left: auto;
      margin-right: auto;

      li {
        padding: 8px 0;
        color: var(--color-text-secondary, #aaa);
        font-size: 0.95rem;

        .todo-icon, .done-icon {
          margin-right: 8px;
        }

        &.done {
          text-decoration: line-through;
          opacity: 0.6;
        }
      }
    }

    .hint-desc {
      font-size: 0.85rem;
      opacity: 0.7;
      margin-top: 20px;
    }
  }
}

.info-card,
.match-card,
.talents-card,
.score-card {
  margin-bottom: 20px;
}

.card-title {
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: var(--color-text, #e0e0e0);
}

.info-grid {
  .info-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);

    &:last-child {
      border-bottom: none;
    }

    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;

      .info-label {
        font-weight: 600;
        color: var(--color-text-secondary, #aaa);
        min-width: 60px;
      }

      .info-value {
        color: var(--color-text, #e0e0e0);
      }

      .strength-tag {
        margin-left: 2px;
      }

      .bazi-text {
        font-size: 1.1rem;
        letter-spacing: 2px;
        font-family: var(--font-mono, monospace);
      }

      .shen-tags {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;

        .shen-tag {
          font-size: 0.85rem;
        }
      }
    }
  }
}

.match-card {
  .match-desc {
    color: var(--color-text-secondary, #aaa);
    font-size: 0.9rem;
    margin-bottom: 16px;
  }
}

.match-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
  word-break: break-word;

  .match-card-item {
    display: flex;
    flex-direction: column;
    padding: 14px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(255, 255, 255, 0.02);
    transition: all 0.3s;
    overflow: hidden;

    &.favorable {
      border-top: 3px solid #67C23A;
      background: rgba(103, 194, 58, 0.06);
    }

    &.unfavorable {
      border-top: 3px solid #F56C6C;
      background: rgba(245, 108, 108, 0.06);
    }

    .card-item-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;

      .grid-label {
        font-weight: 600;
        font-size: 0.95rem;
        color: var(--color-text, #e0e0e0);
      }

      .grid-value-tag {
        flex: 1;
        text-align: center;
      }

      .match-status-icon {
        font-size: 1.1rem;
      }
    }

    .card-item-body {
      display: flex;
      justify-content: center;
      margin-bottom: 10px;

      .shishen-tag {
        font-size: 0.85rem;
        min-width: 80px;
        text-align: center;
      }
    }

    .card-item-footer {
      .match-effect {
        font-size: 0.8rem;
        color: var(--color-text-secondary, #aaa);
        text-align: center;
        display: block;
      }
    }
  }
}

.talents-grid {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 20px;

  .talent-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;

    .talent-label {
      font-size: 0.85rem;
      color: var(--color-text-secondary, #aaa);
    }
  }

  .talent-arrow {
    font-size: 1.2rem;
    color: var(--color-text-secondary, #aaa);
    margin-top: 14px;
  }
}

.talents-result {
  text-align: center;

  .talents-type {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 8px;

    &.type-daji { color: #67C23A; }
    &.type-zhongji { color: #85CE61; }
    &.type-ji { color: #E6A23C; }
    &.type-normal { color: #909399; }
    &.type-xiong { color: #F56C6C; }
  }

  .talents-desc {
    color: var(--color-text-secondary, #aaa);
    font-size: 0.9rem;
    margin-bottom: 12px;
    line-height: 1.6;
  }

  .talents-evaluation {
    font-size: 0.95rem;
    color: var(--color-text, #e0e0e0);

    .eval-label {
      font-weight: 600;
      color: var(--color-text-secondary, #aaa);
    }
  }
}

.score-card {
  .score-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;

    .score-ring {
      :deep(.el-progress__text) {
        .score-number {
          font-size: 2rem;
          font-weight: 700;
          color: var(--color-text, #e0e0e0);
        }
        .score-unit {
          font-size: 0.9rem;
          color: var(--color-text-secondary, #aaa);
        }
      }
    }

    .score-verdict {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 1.15rem;
      font-weight: 600;

      &.verdict-good { color: #67C23A; }
      &.verdict-ok { color: #E6A23C; }
      &.verdict-warn { color: #E6A23C; }
      &.verdict-bad { color: #F56C6C; }

      .verdict-icon {
        font-size: 1.3rem;
      }
    }
  }

  .score-detail {
    margin-bottom: 16px;

    .score-summary {
      color: var(--color-text-secondary, #aaa);
      font-size: 0.9rem;
      line-height: 1.6;
      margin-bottom: 16px;
    }

    .score-breakdown {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .breakdown-item {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 12px;
        border-radius: 20px;
        background: rgba(255, 255, 255, 0.04);

        .breakdown-label {
          font-size: 0.85rem;
          color: var(--color-text-secondary, #aaa);
        }

        .breakdown-value {
          font-weight: 600;
          font-size: 0.9rem;

          &.positive { color: #67C23A; }
          &.negative { color: #F56C6C; }
        }
      }
    }
  }

  .suggestion {
    padding: 12px 16px;
    border-radius: 8px;
    background: rgba(var(--color-accent-rgb, 138, 43, 226), 0.08);
    border-left: 3px solid var(--color-accent, #8A2BE2);
    font-size: 0.9rem;
    line-height: 1.6;
    color: var(--color-text, #e0e0e0);

    .suggestion-label {
      font-weight: 600;
    }
  }
}

// 响应式
@media (max-width: 768px) {
  .match-grid {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 8px;

    .match-card-item {
      padding: 10px;
    }
  }
}
</style>
